import { Box, Flex, Image, useColorMode } from '@chakra-ui/react';
import React, { useState } from 'react';
import { IBet, IObject } from '../../types/bet-split';
import { formatCurrency } from '../../utils/format';
import * as icons from '../icons';
import Text from '../text';

interface WidgetRowMultipleBetProps {
    betObject: IObject;
}

function WidgetRowMultipleBet(props: WidgetRowMultipleBetProps) {
    const { betObject } = props;
    const { colorMode } = useColorMode();
    const [expanded, setExpanded] = useState(true);

    const Icon =
        icons[`${betObject.bets[0].event.sport}Icon` as keyof typeof icons];

    return (
        <Flex direction="column" w="full" gap="16px">
            <Flex
                align="center"
                gap="16px"
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
                <Flex
                    align="center"
                    justify="center"
                    w="50px"
                    h="50px"
                    bg="skyBlue"
                    borderRadius="50%"
                >
                    <Icon />
                </Flex>
                <Text
                    w={{ base: 'calc(100% - 66px)', md: 'auto' }}
                    fontSize="14px"
                    lineHeight="16px"
                    fontWeight="500"
                >
                    Jon Jones - Ciryl Gane - Fight Result - C. Gane - 4 Legs
                </Text>
                <Flex
                    direction={{ base: 'row', md: 'column' }}
                    align={{ base: 'center', md: 'flex-end' }}
                    justify={{
                        base: 'space-between',
                        md: 'flex-start',
                    }}
                    ml="auto"
                    gap="6px"
                    w={{ base: 'full', md: 'auto' }}
                >
                    <Box
                        bg="rgba(120, 184, 244, 0.10)"
                        p="6px 10px"
                        border="1px solid rgba(36, 53, 106, 0.08)"
                        borderRadius={4}
                    >
                        <Text size="sm">KC 38 @ PHI 35</Text>
                    </Box>
                    <Text size="sm">Final 2/12</Text>
                </Flex>
            </Flex>
            <Flex w="full" direction="column">
                <Flex
                    w="full"
                    h="88px"
                    gap={{ base: '0px', md: '10px' }}
                    align="center"
                    bg={
                        colorMode === 'light'
                            ? 'rgba(120,184,244,0.05)'
                            : 'alphaWhite005'
                    }
                    border="1px solid"
                    borderColor={
                        colorMode === 'light'
                            ? 'rgba(36,53,106,0.08)'
                            : 'rgba(223, 230, 239, 0.08)'
                    }
                    borderRadius="6px 6px 0 0"
                    pr={{ base: '12px', md: '24px' }}
                >
                    <Flex
                        w="full"
                        maxW="120px"
                        minW={{ base: '80px', md: '120px' }}
                        h="full"
                        justify="center"
                        align="center"
                    >
                        {betObject.book.logo ? (
                            <Image maxHeight="19px" src={betObject.book.logo} />
                        ) : (
                            <Text>{betObject.book.name}</Text>
                        )}
                    </Flex>
                    <Flex
                        w="full"
                        maxW="170px"
                        h="full"
                        p={{ base: '24px 6px', md: '24px' }}
                        direction="column"
                        align="flex-start"
                        justify="flex-start"
                        gap="10px"
                    >
                        <Text size="xs" lineHeight="14px">
                            At Risk
                        </Text>
                        <Text lineHeight="16px">
                            {formatCurrency(betObject.atRisk)}
                        </Text>
                    </Flex>
                    <Flex
                        w="full"
                        maxW="170px"
                        h="full"
                        p={{ base: '24px 6px', md: '24px' }}
                        direction="column"
                        align="flex-start"
                        justify="flex-start"
                        gap="10px"
                    >
                        <Text size="xs" lineHeight="14px">
                            Odds
                        </Text>
                        <Text lineHeight="16px">
                            {betObject.oddsAmerican || '-'}
                        </Text>
                    </Flex>

                    {betObject.status === 'pending' && (
                        <Flex
                            w="full"
                            maxW="170px"
                            h="full"
                            p={{ base: '24px 6px', md: '24px' }}
                            direction="column"
                            align="flex-start"
                            justify="flex-start"
                            gap="10px"
                        >
                            <Text size="xs" lineHeight="14px">
                                To Win
                            </Text>
                            <Text lineHeight="16px">
                                <Text as="b" lineHeight="inherit">
                                    {formatCurrency(betObject.toWin)}
                                </Text>
                            </Text>
                        </Flex>
                    )}

                    {betObject.netProfit && betObject.status !== 'pending' && (
                        <Flex
                            w="full"
                            maxW="170px"
                            h="full"
                            p={{ base: '24px 6px', md: '24px' }}
                            direction="column"
                            align="flex-start"
                            justify="flex-start"
                            gap="10px"
                        >
                            <Text size="xs" lineHeight="14px">
                                {betObject.status === 'won' ? 'Won' : 'Lost'}
                            </Text>

                            <Text
                                lineHeight="16px"
                                color={
                                    betObject.status === 'won' ? 'green' : 'red'
                                }
                            >
                                <Text as="b" lineHeight="inherit">
                                    {formatCurrency(betObject.netProfit)}
                                </Text>
                            </Text>
                        </Flex>
                    )}

                    <Flex
                        w="24px"
                        minW="24px"
                        h="24px"
                        align="center"
                        justify="center"
                        ml="auto"
                        bg={colorMode === 'light' ? 'white' : 'alphaWhite005'}
                        borderRadius="50%"
                        cursor="pointer"
                        onClick={() => setExpanded((expand) => !expand)}
                        transform={`rotate(${expanded ? 180 : 0}deg)`}
                    >
                        <icons.ChevronDownIcon w="16px" h="16px" />
                    </Flex>
                </Flex>

                {expanded && (
                    <Flex
                        direction="column"
                        gap="24px"
                        w="full"
                        p={{ base: '24px 12px', md: '24px 30px' }}
                        bg="rgba(120, 184, 244, 0.05)"
                        border="1px solid"
                        borderColor={
                            colorMode === 'light'
                                ? '#DFE6EF'
                                : 'rgba(223, 230, 239, 0.08)'
                        }
                        borderTop="none"
                        borderRadius="0 0 8px 8px"
                    >
                        {betObject.bets.map((bet) => (
                            <BetRow key={bet.id} bet={bet} />
                        ))}
                    </Flex>
                )}
            </Flex>
            <Flex align="center" gap="16px">
                <Text size="sm" color="red" whiteSpace="nowrap">
                    100% Lost
                </Text>
                <Box w="full" h="3px" borderRadius="3px" bg="red"></Box>
            </Flex>
        </Flex>
    );
}

interface BetRowProps {
    bet: IBet;
}

function BetRow(props: BetRowProps) {
    const { bet } = props;
    const Icon = icons[`${bet.event.sport}Icon` as keyof typeof icons];

    return (
        <Flex
            w="full"
            align="center"
            gap={{ base: '12px', md: '16px' }}
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
        >
            <Flex
                align="center"
                justify="center"
                w="50px"
                h="50px"
                bg="skyBlue"
                borderRadius="50%"
            >
                <Icon />
            </Flex>
            <Flex
                direction="column"
                w={{
                    base: 'calc(100% - 66px)',
                    md: 'auto',
                }}
                ml={{ base: '4px', md: '0px' }}
            >
                <Text size="sm" fontSize="14px">
                    {bet.event.name}
                </Text>
                <Text size="xs">{bet.bookDescription}</Text>
                <Text size="sm" color="red">
                    100% Lost
                </Text>
            </Flex>
            <Flex
                direction={{ base: 'row', md: 'column' }}
                align={{ base: 'center', md: 'flex-end' }}
                justify={{
                    base: 'space-between',
                    md: 'flex-start',
                }}
                ml="auto"
                gap="6px"
                w={{ base: 'full', md: 'auto' }}
            >
                <Box
                    bg="rgba(120, 184, 244, 0.10)"
                    p="6px 10px"
                    border="1px solid rgba(36, 53, 106, 0.08)"
                    borderRadius={4}
                >
                    <Text size="sm">KC 38 @ PHI 35</Text>
                </Box>
                <Text size="sm">Final 2/12</Text>
            </Flex>
        </Flex>
    );
}

export default WidgetRowMultipleBet;
